import { Modal } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../Buttons/Button";
import { payDividend } from "../../store/actions/stocks/payDividend";
import { getCurrencySymbol } from "../../Helper/getCurrencySymbol";

function PayDividendModal({ open, onClose, stock, onSuccess }) {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.stocks?.payDividendPending);

  const [dividendAmount, setDividendAmount] = useState("");
  const [cutOffDate, setCutOffDate] = useState("");
  const [purificationPercentage, setPurificationPercentage] = useState("");
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setDividendAmount("");
      setCutOffDate("");
      setPurificationPercentage("");
      setTouched({});
    }
  }, [open]);

  const currencySymbol = getCurrencySymbol(stock?.currency) || "₦";

  // Max selectable date is today (no future dates).
  const today = new Date();
  const maxDate = `${today.getFullYear()}-${String(
    today.getMonth() + 1,
  ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  // Per-field validation messages (null = valid).
  const amountError =
    dividendAmount === ""
      ? "Dividend amount is required"
      : Number.isNaN(Number(dividendAmount)) || Number(dividendAmount) <= 0
        ? "Enter an amount greater than 0"
        : null;

  const dateError = !cutOffDate
    ? "Cut-off date is required"
    : cutOffDate > maxDate
      ? "Cut-off date cannot be in the future"
      : null;

  const percentError =
    purificationPercentage === ""
      ? "Purification percentage is required"
      : Number.isNaN(Number(purificationPercentage)) ||
          Number(purificationPercentage) < 0 ||
          Number(purificationPercentage) > 100
        ? "Enter a value between 0 and 100"
        : !/^\d+(\.\d{1,2})?$/.test(String(purificationPercentage))
          ? "Use up to 2 decimal places"
          : null;

  const isValid = !amountError && !dateError && !percentError;

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSubmit = () => {
    if (!isValid) {
      setTouched({ amount: true, date: true, percent: true });
      return;
    }

    dispatch(
      payDividend({
        id: stock?.id,
        data: {
          dividend_amount: Number(dividendAmount),
          cut_off_date: cutOffDate,
          purification_percentage: Number(purificationPercentage),
        },
        callback: () => {
          onClose();
          onSuccess && onSuccess();
        },
      }),
    );
  };

  return (
    <Modal open={open} onClose={() => {}}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 480,
          maxWidth: "92%",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          p: 2,
        }}
      >
        <IconButton
          aria-label="close"
          sx={{ position: "absolute", top: 19, right: 10 }}
          onClick={onClose}
        >
          <ClearIcon />
        </IconButton>

        <div className="w-full p-3">
          <h1 className="font-semibold text-2xl mb-1">Pay Dividend</h1>
          <p className="text-sm mb-6 text-[#475467]">
            Record the total dividend received for{" "}
            {stock?.symbol || "this stock"}. The per-user payout is computed
            internally based on eligible holdings as at the cut-off date.
          </p>

          <label className="text-sm font-[500]">Dividend Amount</label>
          <div
            className={`flex items-center w-full border rounded-lg mt-1 overflow-hidden ${
              touched.amount && amountError
                ? "border-[#FDA29B]"
                : "border-[#D0D5DD]"
            }`}
          >
            <span className="px-3 text-[#475467] bg-[#F9FAFB] py-3 border-r border-[#D0D5DD]">
              {currencySymbol}
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={dividendAmount}
              onChange={(e) => setDividendAmount(e.target.value)}
              onBlur={() => markTouched("amount")}
              placeholder="Enter total dividend amount"
              className="w-full p-3 outline-none"
            />
          </div>
          {touched.amount && amountError ? (
            <p className="text-xs text-[#B42318] mt-1">{amountError}</p>
          ) : null}

          <label className="text-sm font-[500] block mt-4">Cut-off Date</label>
          <input
            type="date"
            value={cutOffDate}
            max={maxDate}
            onChange={(e) => setCutOffDate(e.target.value)}
            onBlur={() => markTouched("date")}
            className={`w-full border rounded-lg p-3 mt-1 outline-none ${
              touched.date && dateError
                ? "border-[#FDA29B]"
                : "border-[#D0D5DD]"
            }`}
          />
          {touched.date && dateError ? (
            <p className="text-xs text-[#B42318] mt-1">{dateError}</p>
          ) : null}

          <label className="text-sm font-[500] block mt-4">
            Purification Percentage
          </label>
          <div
            className={`flex items-center w-full border rounded-lg mt-1 overflow-hidden ${
              touched.percent && percentError
                ? "border-[#FDA29B]"
                : "border-[#D0D5DD]"
            }`}
          >
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={purificationPercentage}
              onChange={(e) => setPurificationPercentage(e.target.value)}
              onBlur={() => markTouched("percent")}
              placeholder="Enter purification percentage"
              className="w-full p-3 outline-none"
            />
            <span className="px-3 text-[#475467] bg-[#F9FAFB] py-3 border-l border-[#D0D5DD]">
              %
            </span>
          </div>
          {touched.percent && percentError ? (
            <p className="text-xs text-[#B42318] mt-1 mb-2">{percentError}</p>
          ) : (
            <div className="mb-6" />
          )}

          <div className="flex mt-2 w-full items-center">
            <CustomButton
              variant="outlined"
              customColor="#fff"
              children="Cancel"
              width="100%"
              height="44px"
              borderColor="#D0D5DD"
              color="#344054"
              margin="0px 25px 0px 0px"
              onClick={onClose}
            />
            <CustomButton
              variant="contained"
              customColor="#159AA8"
              children="Pay Dividend"
              width="100%"
              height="44px"
              loading={loading}
              padding="10px"
              color="#fff"
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default PayDividendModal;
